import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button, Field, Input, Textarea, Chip, Spinner } from "@/components/kit";
import { api, formatApiError } from "@/lib/api";

export default function BasicProfileStep({ reference, onNext, editMode, onBack }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => setForm({
      display_name: data.display_name || "",
      gender_identity: data.gender_identity || "",
      preferred_genders: data.preferred_genders || [],
      city: data.city || "",
      bio: data.bio || "",
      relationship_intention: data.relationship_intention || "",
    }));
  }, []);

  if (!form) return <div className="flex justify-center py-10"><Spinner /></div>;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleGender = (g) =>
    setForm((f) => ({
      ...f,
      preferred_genders: f.preferred_genders.includes(g)
        ? f.preferred_genders.filter((x) => x !== g)
        : [...f.preferred_genders, g],
    }));

  const save = async () => {
    if (!form.display_name || !form.gender_identity || !form.preferred_genders.length || !form.city || !form.bio || !form.relationship_intention) {
      toast.error("Please complete every field to continue.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/profile", form);
      onNext();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-rise">
      <p className="font-sans text-sm text-[#5F6D66] leading-relaxed">
        This is how you'll be introduced. Only your age is shown — never your birth date.
      </p>

      <Field label="Display name">
        <Input value={form.display_name} onChange={(e) => upd("display_name", e.target.value)} placeholder="Your name" data-testid="basic-name-input" />
      </Field>

      <Field label="Your gender identity">
        <div className="flex flex-wrap gap-2">
          {reference.gender_identities.map((g) => (
            <Chip key={g} active={form.gender_identity === g} onClick={() => upd("gender_identity", g)} data-testid={`gender-identity-${g.replace(/\s+/g, "-").toLowerCase()}`}>
              {g}
            </Chip>
          ))}
        </div>
      </Field>

      <Field label="Who you'd like to meet" hint="Select all that apply.">
        <div className="flex flex-wrap gap-2">
          {reference.gender_identities.map((g) => (
            <Chip key={g} active={form.preferred_genders.includes(g)} onClick={() => toggleGender(g)} data-testid={`preferred-gender-${g.replace(/\s+/g, "-").toLowerCase()}`}>
              {g}
            </Chip>
          ))}
        </div>
      </Field>

      <Field label="City">
        <Input value={form.city} onChange={(e) => upd("city", e.target.value)} placeholder="Where you're based" data-testid="basic-city-input" />
      </Field>

      <Field label="What are you looking for?">
        <div className="flex flex-wrap gap-2">
          {reference.relationship_intents.map((r) => (
            <Chip key={r} active={form.relationship_intention === r} onClick={() => upd("relationship_intention", r)} data-testid={`intent-${r.replace(/\s+/g, "-").toLowerCase()}`}>
              {r}
            </Chip>
          ))}
        </div>
      </Field>

      <Field label="A short biography" hint={`${form.bio.length}/600 characters`}>
        <Textarea rows={4} maxLength={600} value={form.bio} onChange={(e) => upd("bio", e.target.value)} placeholder="Share something honest about who you are and what matters to you." data-testid="basic-bio-input" />
      </Field>

      <div className="flex gap-3 pt-2">
        {editMode && <Button variant="outline" className="flex-1" onClick={onBack} data-testid="basic-back-btn">Back</Button>}
        <Button className="flex-1" size="lg" onClick={save} disabled={saving} data-testid="basic-continue-btn">
          {saving ? "Saving…" : "Continue"}
        </Button>
      </div>
    </div>
  );
}
